/**
 * FoodPulseCorrectionsFeed — public timeline of every editorial
 * correction, aggregated from every contributor's `corrections[]` log.
 *
 * URL: /food-pulse/corrections
 *
 * Trust-conscious readers can see what changed on the site, who noted
 * the correction, and jump to the affected guide.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Loader2, Sparkles, Users, ShieldCheck, Utensils, Sprout, BookOpen,
} from "lucide-react";
import { http } from "@/lib/api";
import SeoHead from "@/components/SeoHead";
import FoodPulseTopNav from "@/components/FoodPulseTopNav";
import FoodPulseJourneyNav from "@/components/FoodPulseJourneyNav";
import JsonLd, { breadcrumbSchema } from "@/components/JsonLd";

const ROLE_META = {
  "dietitian":               { label: "Registered Dietitian",     Icon: Utensils,    tint: "text-emerald-700" },
  "food-safety-specialist":  { label: "Food Safety Specialist",   Icon: ShieldCheck, tint: "text-sky-700" },
  "farmer":                  { label: "Working Farmer",           Icon: Sprout,      tint: "text-amber-700" },
  "editor":                  { label: "FoodPulse Editorial Team", Icon: BookOpen,    tint: "text-slate-700" },
};

export default function FoodPulseCorrectionsFeed() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await http.get("/food-pulse/corrections", { params: { limit: 100 } });
        if (!cancelled) setItems(r.data?.items || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const crumb = [
    { name: "FoodPulse",   url: "https://foodpulse.net/food-pulse" },
    { name: "Corrections", url: "https://foodpulse.net/food-pulse/corrections" },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="fp-corrections-feed">
      <SeoHead
        section="food-pulse"
        title="Editorial corrections — FoodPulse"
        description="Every correction logged across every FoodPulse cornerstone guide, sorted newest first. Named contributor. Linked source guide."
      />
      <JsonLd data={breadcrumbSchema(crumb)} />
      <FoodPulseTopNav />
      <FoodPulseJourneyNav />

      <section className="mx-auto max-w-3xl px-4 py-10">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-emerald-700">
          <Sparkles className="h-3.5 w-3.5" /> Editorial corrections
        </div>
        <h1 className="mt-2 font-serif text-3xl text-slate-900 sm:text-4xl">
          What changed on FoodPulse — and why.
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-600 sm:text-base">
          Every published correction — from a shifted USDA storage
          number to a rewritten Section III — is logged here with the
          date, the contributor who reviewed the change, and a link
          straight to the affected guide.
        </p>
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          <Link
            to="/food-pulse/contributors"
            className="inline-flex items-center gap-1 rounded-full border border-emerald-200 px-3 py-1 font-semibold text-emerald-700 hover:bg-emerald-50"
            data-testid="fp-corrections-contributors-link"
          >
            <Users className="h-3 w-3" /> Meet the contributors
          </Link>
          <Link
            to="/food-pulse/methodology"
            className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 font-semibold text-slate-700 hover:bg-slate-50"
            data-testid="fp-corrections-methodology-link"
          >
            <BookOpen className="h-3 w-3" /> Methodology
          </Link>
        </div>

        {loading ? (
          <div className="mt-8 flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading corrections…
          </div>
        ) : items.length === 0 ? (
          <div className="mt-8 rounded-xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center">
            <BookOpen className="mx-auto h-6 w-6 text-slate-300" />
            <h2 className="mt-2 font-serif text-lg text-slate-900">No corrections yet.</h2>
            <p className="mt-1 text-sm text-slate-600">
              We&apos;ll publish every editorial correction here as it happens.
              Spotted something wrong?{" "}
              <Link to="/food-pulse/contact" className="text-emerald-700 hover:underline">
                Tell us and we&apos;ll credit you.
              </Link>
            </p>
          </div>
        ) : (
          <ol className="mt-8 space-y-4" data-testid="fp-corrections-list">
            {items.map((c, i) => {
              const meta = ROLE_META[c.contributor_role] || ROLE_META.editor;
              const { Icon } = meta;
              return (
                <li
                  key={`${c.contributor_slug}-${i}`}
                  data-testid={`fp-correction-row-${i}`}
                  className="relative rounded-2xl border border-slate-200 bg-white p-4 sm:p-5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className="rounded-lg bg-amber-50 p-2 ring-1 ring-amber-200">
                        <Sparkles className="h-4 w-4 text-amber-700" />
                      </div>
                      <div>
                        <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">
                          {c.date || "Date TBD"}
                        </div>
                        <p className="mt-1 text-sm text-slate-800 sm:text-base">
                          {c.note}
                        </p>
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                          <span className="inline-flex items-center gap-1">
                            <Icon className={`h-3 w-3 ${meta.tint}`} />
                            Reviewed by{" "}
                            <Link
                              to={`/food-pulse/contributors/${c.contributor_slug}`}
                              className="font-semibold text-emerald-700 hover:underline"
                            >
                              {c.contributor_name}
                            </Link>
                          </span>
                          {c.guide_slug && (
                            <>
                              <span>·</span>
                              <Link
                                to={`/food-pulse/guides/${c.guide_slug}`}
                                data-testid={`fp-correction-guide-${c.guide_slug}`}
                                className="inline-flex items-center gap-1 font-semibold text-emerald-700 hover:underline"
                              >
                                Read the guide <ArrowRight className="h-3 w-3" />
                              </Link>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </section>
    </div>
  );
}
